import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { Header, Layout, PageContent } from '@loadsmart/miranda-react'

import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { useAbortController } from 'core/ui/hooks/useAbortController'

import { type Carrier } from 'auth/domain/Carrier'
import { useRefreshToken } from 'rate-command-center/hooks/useRefreshToken'
import ExpiredState from 'rate-command-center/ui/ExpiredState'
import { ErrorState } from 'user/ui/ErrorState'

import { usePendingTenders } from './hooks/usePendingTenders'
import { TenderCard } from './TenderCard'
import { TenderListContextProvider, useTenderListContext } from './TenderListContext'
import { type TenderOffer } from './types'

export type TenderListPageProps = {
  tenders?: Array<TenderOffer>
  disabled?: boolean
  carrier?: Carrier
}

const BodyContentWrapper = styled.div`
  max-width: 1024px;
  margin-left: auto;
  margin-right: auto;
`

function TenderListContent({ disabled = false, carrier }: TenderListPageProps) {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const token = params.get('token') ?? ''
  const tenderUUID = params.get('tender_uuid') ?? ''

  const { pendingTenders, isTokenExpired, pendingTendersRequestStatus } = usePendingTenders({
    token,
  })

  const { emailRecipient, refreshToken, refreshTokenStatus } = useRefreshToken()

  const abortController = useAbortController()

  const { sourceTenderUUID } = useTenderListContext()

  const orderedTenders = useMemo(() => {
    if (!sourceTenderUUID) return pendingTenders

    const sourceTender = pendingTenders?.find(tender => tender.id === sourceTenderUUID)
    if (!sourceTender) return pendingTenders

    const otherTenders = pendingTenders?.filter(tender => tender.id !== sourceTenderUUID) ?? []
    return [sourceTender, ...otherTenders]
  }, [pendingTenders, sourceTenderUUID])

  useEffect(() => {
    const data = { tender_uuid: tenderUUID }

    if (isTokenExpired) {
      refreshToken({ token, data, config: abortController })
    }
  }, [abortController, isTokenExpired, refreshToken, token, tenderUUID])

  return (
    <AsyncContent
      status={pendingTendersRequestStatus}
      loadingState={<LoadingState />}
      errorState={
        isTokenExpired ? (
          <ExpiredState
            showHeader={false}
            emailRecipient={emailRecipient}
            refreshTokenStatus={refreshTokenStatus}
          />
        ) : (
          <ErrorState />
        )
      }
    >
      <PageContent>
        <Header>
          <Header.Content>
            <Header.Title>Tender Offers</Header.Title>
          </Header.Content>
        </Header>
        <PageContent.Body>
          <BodyContentWrapper>
            <Layout.Stack gap="spacing-4">
              {orderedTenders?.map(tenderItem => (
                <TenderCard
                  key={tenderItem.id}
                  tender={tenderItem}
                  disabled={disabled}
                  carrier={tenderItem.carrier}
                  highlighted={tenderItem.id === sourceTenderUUID}
                />
              ))}
            </Layout.Stack>
          </BodyContentWrapper>
        </PageContent.Body>
      </PageContent>
    </AsyncContent>
  )
}

function TenderListPage(props: TenderListPageProps) {
  return (
    <TenderListContextProvider>
      <TenderListContent {...props} />
    </TenderListContextProvider>
  )
}

export default TenderListPage
